import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Rating,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
  SvgIcon,
  Link,
  Checkbox,
  ListItemText,
  ListItemIcon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  FormHelperText,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Modal,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Search as SearchIcon,
  Instagram as InstagramIcon,
  Google as GoogleIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  ContentCopy as ContentCopyIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  AssignmentTurnedIn as ResolvedIcon,
  HistoryToggleOff as NotResolvedIcon,
  NewReleases as NewIcon,
  Pending as InProgressIcon,
  History as HistoryIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  Share as ShareIcon,
  Store as StoreIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../app/store';
import { fetchPlaces } from '../../features/googleReviews/googleReviewsSlice';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../config/api';
import { FilterProps } from './FeedbackDashboard';
import { UnifiedFeedback } from '../../types/unifiedFeedback';
import { FeedbackActionButtons } from '../../components/feedback/FeedbackActionButtons';
import { FeedbackDetailDialog } from '../../components/feedback/FeedbackDetailDialog';
import { ComplaintStatusDialog } from '../../components/feedback/ComplaintStatusDialog';
import { ComplaintHistoryDialog } from '../../components/feedback/ComplaintHistoryDialog';

// ... UnifiedFeedback, ProcessedBrand, EditDialog, TikTokIcon definitions (copy from UnifiedFeedbackPage) ...

// Copy the interfaces and helper components from UnifiedFeedbackPage here
// ... existing code ...

interface GoogleReview {
  id: string;
  text: string;
  stars: number;
  reviewer_name: string;
  published_at: string;
  reviewer_photo_url?: string;
  detailed_ratings?: { category: string; rating: number }[];
  review_images?: string[];
}

interface ProcessedBrand {
  brand_id: string;
  brand_name: string;
  platforms: ('instagram' | 'google' | 'tiktok' | 'facebook' | 'gcc' | 'dm')[];
}

interface EditDialogProps {
  open: boolean;
  comment: UnifiedFeedback | null;
  onClose: () => void;
  onSave: (id: string, updates: { sentiment?: string; category?: string; priority?: number | string }) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({ open, comment, onClose, onSave }) => {
  const [sentiment, setSentiment] = useState(comment?.sentiment || '');
  const [category, setCategory] = useState(comment?.category || '');
  const [priority, setPriority] = useState<number | string>(comment?.priority || '');

  useEffect(() => {
    setSentiment(comment?.sentiment || '');
    setCategory(comment?.category || '');
    setPriority(comment?.priority || '');
  }, [comment]);

  const handleSave = () => {
    if (comment?.id) {
      onSave(comment.id, {
        sentiment: sentiment || undefined,
        category: category || undefined,
        priority: priority || undefined,
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Sentiment</InputLabel>
            <Select
              value={sentiment}
              onChange={(e) => setSentiment(e.target.value)}
              label="Sentiment"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="positive">Positive</MenuItem>
              <MenuItem value="negative">Negative</MenuItem>
              <MenuItem value="neutral">Neutral</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="feature">Feature</MenuItem>
              <MenuItem value="improvement">Improvement</MenuItem>
              <MenuItem value="question">Question</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              label="Priority"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={1}>High</MenuItem>
              <MenuItem value={2}>Medium</MenuItem>
              <MenuItem value={3}>Low</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const TikTokIcon = (props: any) => {
  const theme = useTheme();
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ color: theme.palette.text.primary, ...props.sx }}>
      <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 015.4 5.82C4.1 4.5 4.1 2.37 5.4 1.06 6.7-.25 8.84-.25 10.16 1.06c.63.62 1.109 1.39 1.39 2.24h3.17v7.54c0 1.45 1.19 2.64 2.64 2.64s2.64-1.19 2.64-2.64l-.01-3.89h1.98c1.2-.01 2.17-.98 2.17-2.18s-.97-2.17-2.17-2.17h-1.98c-.06-2.2-.93-4.27-2.41-5.79-1.91-1.91-4.54-2.84-7.18-2.64-4.47.34-8.14 3.98-8.52 8.45-.26 3.02.87 5.88 3.03 8.04s5.02 3.29 8.04 3.03c4.47-.38 8.11-4.05 8.45-8.52.19-2.64-.73-5.27-2.64-7.18z"/>
    </SvgIcon>
  );
};

const GCCIcon = (props: any) => {
  return (
    <SvgIcon {...props}>
      <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z" />
    </SvgIcon>
  );
};

// Add DM icon component
const DMIcon = (props: any) => {
  return (
    <SvgIcon {...props}>
      <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
    </SvgIcon>
  );
};

interface StatusOption {
  value: 'new' | 'in_progress' | 'resolved' | 'not_resolved';
  label: string;
  color: 'info' | 'warning' | 'success' | 'error';
  icon: React.ReactElement;
}


// Interface for GCC details
interface QuestionnaireItem {
  question: string;
  answer: string;
  pooranswer: string;
}

interface GCCDetail {
  customer_name: string;
  customer_phone: string;
  sales_number: string;
  brand_name: string;
  outlet_name: string;
  submit_date: string;
  outlet_address?: string;
  outlet_phone?: string;
  status?: string;
  questionnaire: QuestionnaireItem[];
}

interface Outlet {
  outlet_id: string;
  brand_id: string;
  brand_name: string;
}

interface OutletPIC {
  user_id: string;
  manager_name: string;
  email: string;
  job_title: string;
  position_type: string;
  brand_id: string;
  brand_name: string;
  outlet_id: string;
}

interface ComplaintManagementPageProps {
  filterProps: FilterProps;
}

const ComplaintManagementPage = ({ filterProps }: ComplaintManagementPageProps): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [unifiedFeedback, setUnifiedFeedback] = useState<UnifiedFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState(filterProps.searchTerm);
  const [searchTerm, setSearchTerm] = useState(filterProps.searchTerm);
  const [platformFilter, setPlatformFilter] = useState<'all' | 'instagram' | 'google' | 'tiktok' | 'facebook' | 'gcc' | 'dm'>('all');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'priority'>('latest');
  const [priorityFilter, setPriorityFilter] = useState<'all' | '1' | '2' | '3'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<ProcessedBrand[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState<UnifiedFeedback | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('year'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [copySuccessMessage, setCopySuccessMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'in_progress' | 'resolved' | 'not_resolved'>('all');
  const [statusOptions] = useState<StatusOption[]>([
    { value: 'new', label: 'New', color: 'info', icon: <NewIcon /> },
    { value: 'in_progress', label: 'In Progress', color: 'warning', icon: <InProgressIcon /> },
    { value: 'resolved', label: 'Resolved', color: 'success', icon: <ResolvedIcon /> },
    { value: 'not_resolved', label: 'Not Resolved', color: 'error', icon: <NotResolvedIcon /> }
  ]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [statusCounts, setStatusCounts] = useState<{
    [platform in 'instagram' | 'google' | 'tiktok' | 'facebook' | 'gcc' | 'dm' | 'all']: {
      new: number;
      in_progress: number;
      resolved: number;
      not_resolved: number;
      total: number;
    };
  }>({
    instagram: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 },
    google: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 },
    tiktok: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 },
    facebook: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 },
    gcc: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 },
    dm: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 },
    all: { new: 0, in_progress: 0, resolved: 0, not_resolved: 0, total: 0 }
  });
  const [userPositionType, setUserPositionType] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{user_id?: string; position_type?: string} | null>(null);
  const [isUserOTL, setIsUserOTL] = useState(false);
  
  // GCC details modal state
  const [gccModalOpen, setGccModalOpen] = useState(false);
  const [selectedGccId, setSelectedGccId] = useState<string>('');
  const [gccDetail, setGccDetail] = useState<GCCDetail | null>(null);
  const [loadingGccDetail, setLoadingGccDetail] = useState(false);
  const [activeGccTab, setActiveGccTab] = useState(0);

  // Add these new state variables
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [selectedOutlet, setSelectedOutlet] = useState<string>('');
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  const [outletError, setOutletError] = useState<string | null>(null);

  useEffect(() => {
    void fetchUserInfo();
    void fetchBrands();
    void fetchCategories();
  }, []);

  useEffect(() => {
    void fetchFeedback();
    void fetchStatusCounts();
  }, [
    page, 
    searchTerm, 
    platformFilter, 
    sortBy, 
    priorityFilter, 
    categoryFilter, 
    selectedBrands, 
    startDate, 
    endDate, 
    statusFilter,
    userInfo,
    selectedOutlet // Add this line
  ]);
  
  useEffect(() => {
    setSearchInput(filterProps.searchTerm);
    setSearchTerm(filterProps.searchTerm);
    // Update other filter states as needed and trigger data fetch
    fetchFeedback();
  }, [filterProps]);

  // Handle search functionality
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1); // Reset to first page when searching
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    if (selectedBrands.length > 0) {
      void fetchOutlets();
    } else {
      setOutlets([]);
      setSelectedOutlet('');
    }
  }, [selectedBrands]);

  const fetchUserInfo = async () => {
    try {
      const response = await api.get('/auth/user-info');
      if (response.data.success) {
        const userData = response.data.data;
        setUserInfo(userData);
        setIsUserOTL(userData.position_type === 'OTL');
        
        // For OTL users, automatically filter by in_progress status
        // since they should only see what's assigned to them
        if (userData.position_type === 'OTL') {
          setStatusFilter('in_progress');
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/feedback/brands');
      if (response.data?.data) {
        const processedBrands = response.data.data.reduce((acc: ProcessedBrand[], currentBrand: any) => {
          const existingBrand = acc.find(b => b.brand_id === currentBrand.brand_id);
          if (existingBrand) {
            existingBrand.platforms = Array.from(new Set([...existingBrand.platforms, currentBrand.platform]));
          } else {
            acc.push({
              brand_id: currentBrand.brand_id,
              brand_name: currentBrand.brand_name,
              platforms: [currentBrand.platform]
            });
          }
          return acc;
        }, []);
        setAvailableBrands(processedBrands);
      } else {
        // Fallback with an empty array if API doesn't return proper data
        setAvailableBrands([]);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      // Fallback with an empty array in case of error
      setAvailableBrands([]);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await api.get('/feedback/categories');
      setAvailableCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchOutlets = async () => {
    setLoadingOutlets(true);
    setOutletError(null);
    try {
      const params = selectedBrands.length === 1 ? { brand_id: selectedBrands[0] } : {};
      const response = await api.get('/brand-management/outlets', { params });
      
      if (response.data.success) {
        setOutlets(response.data.data);
      } else {
        setOutletError('Failed to fetch outlets');
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
      setOutletError('Error loading outlets');
    } finally {
      setLoadingOutlets(false);
    }
  };

  const fetchFeedback = async () => {
    setLoading(true);
    
    try {
      const params: any = {
        page,
        limit: rowsPerPage,
        platform: platformFilter,
        sort_by: sortBy,
        start_date: startDate?.toISOString(),
        end_date: endDate ? endDate.endOf('day').toISOString() : undefined,
        sentiment: 'negative',
      };

      // For OTL users, we always want to filter by assigned to them and in_progress status
      if (isUserOTL && userInfo?.user_id) {
        params.assigned_to = userInfo.user_id;
        params.status = 'in_progress'; // Force status filter for OTL users
      } else {
        // For non-OTL users, apply normal status filtering
        if (statusFilter !== 'all') {
          params.status = statusFilter;
        }
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (priorityFilter !== 'all') {
        params.priority = priorityFilter;
      }

      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      if (selectedBrands.length > 0) {
        params.brands = selectedBrands;
      }

      // Add outlet filter
      if (selectedOutlet) {
        params.outlet_id = selectedOutlet;
        console.log('Filtering by outlet_id:', selectedOutlet);
      }

      console.log('Feedback request params:', params);

      const response = await api.get('/feedback/unified', { params });
      
      setUnifiedFeedback(response.data.data);
      setTotal(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError('Failed to fetch feedback data');
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      const params: any = {
        platform: platformFilter !== 'all' ? platformFilter : undefined,
        brands: selectedBrands.length > 0 ? selectedBrands.join(',') : undefined,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      };
      
      // If user is OTL, only count complaints assigned to them
      if (isUserOTL && userInfo?.user_id) {
        params.assigned_to = userInfo.user_id;
      }
      
      // Add outlet filter
      if (selectedOutlet) {
        params.outlet_id = selectedOutlet;
        console.log('Status counts filtered by outlet_id:', selectedOutlet);
      }
      
      console.log('Status counts params:', params);
      
      const response = await api.get('/feedback/complaint-status-counts', { params });
      
      if (response.data && response.data.success) {
        setStatusCounts(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching status counts:', error);
      
      // Don't show error alert for status counts - just log to console
      if (error.response?.data?.error && error.response.data.error.includes("Table") && error.response.data.error.includes("doesn't exist")) {
        console.warn('Database tables for complaint status not configured yet');
      }
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleEditClick = (comment: UnifiedFeedback) => {
    setSelectedComment(comment);
    setEditDialogOpen(true);
  };

  const handleEditSave = async (id: string, updates: { sentiment?: string; category?: string; priority?: number | string }) => {
    try {
      const response = await api.patch(`/comments/${id}/metadata`, updates);
      setUnifiedFeedback((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, ...response.data } : item
        )
      );
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handlePriorityFilterChange = (value: 'all' | '1' | '2' | '3') => {
    setPriorityFilter(value);
    setPage(1);
  };

  const handleCategoryFilterChange = (value: 'all' | string) => {
    setCategoryFilter(value as 'all' | string);
    setPage(1);
  };

  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccessMessage(`${type} copied to clipboard!`);
        setTimeout(() => setCopySuccessMessage(null), 3000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  const handleStatusFilterChange = (value: 'all' | 'new' | 'in_progress' | 'resolved' | 'not_resolved') => {
    // For OTL users, they can only see in_progress items assigned to them
    if (isUserOTL && value !== 'in_progress') {
      // Don't allow them to change status filter away from in_progress
      return;
    }
    
    setStatusFilter(value);
    setPage(1); // Reset page to 1 when status filter changes
  };

  const handleStatusClick = (feedback: UnifiedFeedback) => {
    setSelectedComment(feedback);
    setStatusDialogOpen(true);
  };

  const handleHistoryClick = (feedback: UnifiedFeedback) => {
    setSelectedComment(feedback);
    setHistoryDialogOpen(true);
  };

  const handleDetailClick = (feedback: UnifiedFeedback) => {
    setSelectedComment(feedback);
    setDetailDialogOpen(true);
  };

  const handleActionCopy = (feedback: UnifiedFeedback) => {
    handleCopyToClipboard(feedback.text || 'No text content', 'text');
  };

  const handleStatusSave = async (id: string, updates: { status: string; note: string; outlet_pic_id?: string }) => {
    try {
      const response = await api.patch(`/feedback/complaint-status/${id}`, updates);
      
      setUnifiedFeedback((prev) =>
        prev.map((item) =>
          item.id === id ? { 
            ...item, 
            complaint_status: response.data.data.complaint_status,
            complaint_note: response.data.data.complaint_note,
            // Add outlet_pic_id if it's available in the response
            ...(response.data.data.outlet_pic_id && { outlet_pic_id: response.data.data.outlet_pic_id }),
            ...(response.data.data.outlet_pic_name && { outlet_pic_name: response.data.data.outlet_pic_name })
          } : item
        )
      );

      // Refresh status counts after status change
      fetchStatusCounts();

      setCopySuccessMessage('Status updated successfully!');
      setTimeout(() => setCopySuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error updating status:', error);
      
      // Check for database table errors
      if (error.response?.data?.error && error.response.data.error.includes("Table") && error.response.data.error.includes("doesn't exist")) {
        setCopySuccessMessage('Database setup incomplete. Please contact system administrator.');
      } else {
        setCopySuccessMessage('Failed to update status');
      }
      
      setTimeout(() => setCopySuccessMessage(null), 5000);
    }
  };

  const getStatusColor = (status?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    if (!status) return 'default';
    switch (status) {
      case 'new': return 'info';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'not_resolved': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'new':
        return <NewIcon color="info" />;
      case 'in_progress':
        return <InProgressIcon color="warning" />;
      case 'resolved':
        return <ResolvedIcon color="success" />;
      case 'not_resolved':
        return <NotResolvedIcon color="error" />;
      default:
        return <TimelineIcon />;
    }
  };

  const getStatusLabel = (status?: string): string => {
    if (!status) return 'New';
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Get status chip for display
  const getStatusChip = (status?: string) => {
    if (!status) return <Chip size="small" label="New" color="info" icon={<NewIcon />} />;
    
    switch (status.toLowerCase()) {
      case 'new':
        return <Chip size="small" label="New" color="info" icon={<NewIcon />} />;
      case 'in_progress':
        return <Chip size="small" label="In Progress" color="warning" icon={<InProgressIcon />} />;
      case 'resolved':
        return <Chip size="small" label="Resolved" color="success" icon={<ResolvedIcon />} />;
      case 'not_resolved':
        return <Chip size="small" label="Not Resolved" color="error" icon={<NotResolvedIcon />} />;
      default:
        return <Chip size="small" label={status} color="default" />;
    }
  };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return dayjs(dateString).format('DD MMM YYYY HH:mm');
    } catch (e) {
      console.error('Invalid date format:', dateString, e);
      return 'Invalid date';
    }
  };

  // Handle GCC click to show details
  const handleGccClick = async (feedback: UnifiedFeedback) => {
    if (!feedback.brand_identifier) {
      setCopySuccessMessage('No GCC reference ID found for this feedback');
      setTimeout(() => setCopySuccessMessage(null), 3000);
      return;
    }
    
    setSelectedGccId(feedback.brand_identifier);
    setLoadingGccDetail(true);
    setGccDetail(null);
    setGccModalOpen(true);
    
    try {
      const response = await api.get('/comment-card/all-gcc-data', {
        params: { salesNumber: feedback.brand_identifier }
      });
      
      if (response.data.success) {
        setGccDetail(response.data.data);
      } else {
        console.error('Failed to fetch GCC data:', response.data.message);
        setCopySuccessMessage('Failed to fetch GCC details');
        setTimeout(() => setCopySuccessMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error fetching GCC data:', error);
      setCopySuccessMessage('Error fetching GCC details');
      setTimeout(() => setCopySuccessMessage(null), 3000);
    } finally {
      setLoadingGccDetail(false);
    }
  };

  // Handle close GCC modal
  const handleCloseGccModal = () => {
    setGccModalOpen(false);
    setSelectedGccId('');
    setGccDetail(null);
  };

  // Helper function to render status counter card
  const renderStatusCard = (statusValue: 'new' | 'in_progress' | 'resolved' | 'not_resolved', platform: 'instagram' | 'google' | 'tiktok' | 'facebook' | 'gcc' | 'dm' | 'all') => {
    // For OTL users, only show the in_progress card
    if (isUserOTL && statusValue !== 'in_progress') {
      return null;
    }
    
    const statusOption = statusOptions.find(option => option.value === statusValue);
    const count = statusCounts[platform]?.[statusValue] || 0;
    const platformData = statusCounts[platform] || { total: 0 };
    
    return (
      <Card 
        sx={{ 
          minWidth: 120, 
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
        onClick={() => handleStatusFilterChange(statusValue)}
      >
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            {statusOption?.icon}
            <Typography variant="h5" color={`${statusOption?.color}.main`}>
              {count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isUserOTL && statusValue === 'in_progress' ? 'Assigned to me' : statusOption?.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round((count / (platformData.total || 1)) * 100)}% of {platform === 'all' ? 'All platforms' : 
                platform === 'instagram' ? 'Instagram' :
                platform === 'google' ? 'Google' :
                platform === 'tiktok' ? 'TikTok' :
                platform === 'dm' ? 'Direct Message' : 'GCC'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Add a column to display assigned outlet in the feedback list
  const renderFeedbackListItem = (feedback: UnifiedFeedback) => {
    return (
      <TableRow
        hover
        key={feedback.id}
        onClick={() => handleDetailClick(feedback)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell>
          <Box display="flex" alignItems="center">
            <Avatar
              src={feedback.authorImage || undefined}
              alt={feedback.author}
              sx={{ mr: 2, width: 40, height: 40 }}
            />
            <Box>
              <Typography variant="subtitle2">{feedback.author}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(feedback.created_at)}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {!feedback.text ? 'No text content' : 
              feedback.text.length > 100 ? `${feedback.text.substring(0, 100)}...` : feedback.text
            }
          </Typography>
          {feedback.platform === 'gcc' && feedback.brand_identifier && (
            <Chip 
              size="small" 
              label={`Ref: ${feedback.brand_identifier}`}
              color="primary"
              variant="outlined"
              onClick={() => handleGccClick(feedback)}
              sx={{ mt: 0.5, cursor: 'pointer' }}
            />
          )}
        </TableCell>
        <TableCell align="center">
          {feedback.platform === 'instagram' && <InstagramIcon sx={{ color: '#E1306C' }} />}
          {feedback.platform === 'google' && <GoogleIcon sx={{ color: '#4285F4' }} />}
          {feedback.platform === 'tiktok' && <TikTokIcon sx={{ color: '#000000' }} />}
          {feedback.platform === 'gcc' && <GCCIcon />}
          {feedback.platform === 'dm' && <DMIcon sx={{ color: '#1976D2' }} />}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {feedback.platform === 'instagram' ? 'Instagram' :
             feedback.platform === 'google' ? 'Google' :
             feedback.platform === 'tiktok' ? 'TikTok' :
             feedback.platform === 'dm' ? 'Direct Message' : 'GCC'}
          </Typography>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">{feedback.brand_name || '-'}</Typography>
            {feedback.brand_name && (
              <Chip
                size="small"
                label={feedback.platform}
                icon={
                  feedback.platform === 'instagram' ? <InstagramIcon sx={{ fontSize: '1rem' }} /> :
                  feedback.platform === 'google' ? <GoogleIcon sx={{ fontSize: '1rem' }} /> :
                  feedback.platform === 'tiktok' ? <TikTokIcon sx={{ fontSize: '1rem' }} /> :
                  feedback.platform === 'dm' ? <DMIcon sx={{ fontSize: '1rem' }} /> :
                  <GCCIcon sx={{ fontSize: '1rem' }} />
                }
                variant="outlined"
                sx={{ 
                  height: 20,
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center">
              <Chip
                size="small"
                icon={getStatusIcon(feedback.complaint_status || 'new')}
                label={getStatusLabel(feedback.complaint_status)}
                color={getStatusColor(feedback.complaint_status)}
              />
            </Box>
            {feedback.complaint_status === 'in_progress' && (
              <Tooltip title={feedback.outlet_pic_name ? `Assigned to: ${feedback.outlet_pic_name}` : "No PIC assigned"}>
                <Chip
                  size="small"
                  icon={<PersonIcon fontSize="small" />}
                  label={feedback.outlet_pic_name || "Unassigned"}
                  variant="outlined"
                  color={feedback.outlet_pic_name ? "primary" : "default"}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <FeedbackActionButtons
            feedback={feedback}
            layout="row"
            onViewDetail={handleDetailClick}
            onEditStatus={handleStatusClick}
            onViewHistory={handleHistoryClick}
            onEditMetadata={handleEditClick}
            onViewGcc={handleGccClick}
            onCopy={handleActionCopy}
            showMetadataEdit={feedback.platform !== 'gcc'}
          />
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isUserOTL ? "My Assigned Complaints" : "Need Action"}
        </Typography>
        
        {/* Status Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {isUserOTL 
                ? "Complaints Assigned to Me" 
                : (platformFilter === 'all' ? 'All Platforms' : 
                  platformFilter === 'instagram' ? 'Instagram' :
                  platformFilter === 'google' ? 'Google' :
                  platformFilter === 'tiktok' ? 'TikTok' :
                  platformFilter === 'dm' ? 'Direct Message' : 'GCC') + " Status Summary"}
            </Typography>
              
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ 
              backgroundColor: 'background.paper', 
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}>
              <Grid container spacing={2}>
                {/* Search Bar */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search complaints..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleSearch}
                            sx={{ p: 0.5 }}
                          >
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {/* Platform and Brands Filter Row */}
                <Grid item xs={12} md={8}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 120, flexGrow: 1 }}>
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={platformFilter}
                        label="Platform"
                        onChange={(e) => setPlatformFilter(e.target.value as 'all' | 'instagram' | 'google' | 'tiktok' | 'gcc' | 'dm')}
                      >
                        <MenuItem value="all">All Platforms</MenuItem>
                        <MenuItem value="instagram">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InstagramIcon fontSize="small" sx={{ color: '#E1306C' }} />
                            <span>Instagram</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="google">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GoogleIcon fontSize="small" sx={{ color: '#4285F4' }} />
                            <span>Google</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="tiktok">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TikTokIcon />
                            <span>TikTok</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="gcc">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GCCIcon />
                            <span>GCC</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="dm">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DMIcon fontSize="small" sx={{ color: '#1976D2' }} />
                            <span>Direct Messages</span>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120, flexGrow: 2 }}>
                      <InputLabel>Brands</InputLabel>
                      <Select
                        multiple
                        value={selectedBrands}
                        onChange={(e) => setSelectedBrands(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        label="Brands"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const brand = availableBrands.find(b => b.brand_id === value);
                              return brand && (
                                <Chip 
                                  key={value} 
                                  label={brand.brand_name}
                                  size="small"
                                  icon={
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      {brand.platforms.includes('instagram') && <InstagramIcon sx={{ fontSize: '1rem' }} />}
                                      {brand.platforms.includes('google') && <GoogleIcon sx={{ fontSize: '1rem' }} />}
                                      {brand.platforms.includes('tiktok') && <TikTokIcon sx={{ fontSize: '1rem' }} />}
                                    </Box>
                                  }
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {availableBrands.map((brand) => (
                          <MenuItem key={brand.brand_id} value={brand.brand_id}>
                            <Checkbox checked={selectedBrands.includes(brand.brand_id)} />
                            <ListItemIcon>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {brand.platforms.includes('instagram') && <InstagramIcon />}
                                {brand.platforms.includes('google') && <GoogleIcon />}
                                {brand.platforms.includes('tiktok') && <TikTokIcon />}
                              </Box>
                            </ListItemIcon>
                            <ListItemText primary={brand.brand_name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* Add the outlet filter here */}
                    <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
                      <InputLabel>Outlet</InputLabel>
                      <Select
                        value={selectedOutlet}
                        onChange={(e) => setSelectedOutlet(e.target.value)}
                        label="Outlet"
                        disabled={loadingOutlets || selectedBrands.length === 0}
                      >
                        <MenuItem value="">
                          <em>All Outlets</em>
                        </MenuItem>
                        {loadingOutlets ? (
                          <MenuItem disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={20} />
                              <span>Loading outlets...</span>
                            </Box>
                          </MenuItem>
                        ) : outlets.map((outlet) => (
                          <MenuItem key={outlet.outlet_id} value={outlet.outlet_id}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2">{outlet.outlet_id}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {outlet.brand_name}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {outletError && (
                        <FormHelperText error>{outletError}</FormHelperText>
                      )}
                    </FormControl>
                  </Stack>
                </Grid>
                {/* Filters and Date Range Row */}
                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {(platformFilter === 'instagram' || platformFilter === 'tiktok' || platformFilter === 'all') && (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Priority</InputLabel>
                          <Select
                            value={priorityFilter}
                            label="Priority"
                            onChange={(e) => handlePriorityFilterChange(e.target.value as 'all' | '1' | '2' | '3')}
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="1">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                                <span>High</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="2">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                                <span>Medium</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="3">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                                <span>Low</span>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      {(platformFilter === 'instagram' || platformFilter === 'tiktok' || platformFilter === 'all') && (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={categoryFilter}
                            label="Category"
                            onChange={(e) => handleCategoryFilterChange(e.target.value as 'all' | string)}
                          >
                            <MenuItem value="all">All</MenuItem>
                            {loadingCategories ? (
                              <MenuItem disabled>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={20} />
                                  <span>Loading categories...</span>
                                </Box>
                              </MenuItem>
                            ) : (
                              availableCategories.map((category: string) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                    <Box sx={{ ml: 'auto' }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{ 
                              textField: { 
                                size: 'small',
                                sx: { width: { xs: '100%', sm: 'auto' } } 
                              } 
                            }}
                          />
                          <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{ 
                              textField: { 
                                size: 'small',
                                sx: { width: { xs: '100%', sm: 'auto' } } 
                              } 
                            }}
                            minDate={startDate || undefined}
                          />
                        </Stack>
                      </LocalizationProvider>
                    </Box>
                  </Stack>
                    </Grid>
                {/* Active Filters Display */}
                {(priorityFilter !== 'all' || categoryFilter !== 'all' || selectedBrands.length > 0) && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                        Active filters:
                      </Typography>
                      <Chip 
                        label="Sentiment: negative" 
                        size="small" 
                        color="error" 
                        sx={{ mr: 1 }}
                      />
                      {priorityFilter !== 'all' && (
                        <Chip 
                          label={`Priority: ${
                            priorityFilter === '1' ? 'High' :
                            priorityFilter === '2' ? 'Medium' :
                            'Low'
                          }`} 
                          size="small" 
                          onDelete={() => handlePriorityFilterChange('all')}
                          color={
                            priorityFilter === '1' ? 'error' :
                            priorityFilter === '2' ? 'warning' : 'success'
                          }
                        />
                      )}
                      {categoryFilter !== 'all' && (
                        <Chip 
                          label={`Category: ${categoryFilter}`} 
                          size="small" 
                          onDelete={() => handleCategoryFilterChange('all')}
                          color="info"
                        />
                      )}
                      {/* Add chip for outlet filter */}
                      {selectedOutlet && (
                        <Chip 
                          icon={<StoreIcon fontSize="small" />}
                          label={`Outlet: ${selectedOutlet}`} 
                          size="small" 
                          onDelete={() => setSelectedOutlet('')}
                          color="primary"
                        />
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>
            <Grid container spacing={2}>
              {statusOptions.map((status) => (
                <Grid item key={status.value} xs={6} sm={3} md={3} lg={3}>
                  {renderStatusCard(status.value, platformFilter)}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
        
        {/* Platform-specific Status Summary - hide for OTL users */}
        {!isUserOTL && platformFilter === 'all' && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Status by Platform</Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Card sx={{ flex: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <InstagramIcon sx={{ color: '#E1306C' }} />
                    <Typography variant="subtitle1">Instagram ({statusCounts?.instagram?.total || 0})</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {statusOptions.map((status) => (
                      <Chip
                        key={`instagram-${status.value}`}
                        icon={status.icon}
                        label={`${status.label}: ${statusCounts?.instagram?.[status.value] || 0}`}
                        color={status.color as any}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Card>
                
                <Card sx={{ flex: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <GoogleIcon sx={{ color: '#4285F4' }} />
                    <Typography variant="subtitle1">Google ({statusCounts?.google?.total || 0})</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {statusOptions.map((status) => (
                      <Chip
                        key={`google-${status.value}`}
                        icon={status.icon}
                        label={`${status.label}: ${statusCounts?.google?.[status.value] || 0}`}
                        color={status.color as any}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Card>

                <Card sx={{ flex: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <TikTokIcon />
                    <Typography variant="subtitle1">TikTok ({statusCounts?.tiktok?.total || 0})</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {statusOptions.map((status) => (
                      <Chip
                        key={`tiktok-${status.value}`}
                        icon={status.icon}
                        label={`${status.label}: ${statusCounts?.tiktok?.[status.value] || 0}`}
                        color={status.color as any}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Card>
                
                <Card sx={{ flex: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <GCCIcon />
                    <Typography variant="subtitle1">GCC ({statusCounts?.gcc?.total || 0})</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {statusOptions.map((status) => (
                      <Chip
                        key={`gcc-${status.value}`}
                        icon={status.icon}
                        label={`${status.label}: ${statusCounts?.gcc?.[status.value] || 0}`}
                        color={status.color as any}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Card>

                <Card sx={{ flex: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <DMIcon sx={{ color: '#1976D2' }} />
                    <Typography variant="subtitle1">Direct Messages ({statusCounts?.dm?.total || 0})</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {statusOptions.map((status) => (
                      <Chip
                        key={`dm-${status.value}`}
                        icon={status.icon}
                        label={`${status.label}: ${statusCounts?.dm?.[status.value] || 0}`}
                        color={status.color as any}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Card>
              </Box>
            </Grid>
          </Grid>
        )}
      
        {/* Feedback data table */}
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Author</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell align="center">Platform</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {platformFilter === 'facebook' ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        py={8}
                        textAlign="center"
                      >
                        <FacebookIcon sx={{ fontSize: 64, color: '#1877F2', mb: 2 }} />
                        <Typography variant="h5" color="textSecondary" gutterBottom>
                          No Data
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                          Facebook integration is coming soon!
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : unifiedFeedback.length > 0 ? (
                  unifiedFeedback.map(feedback => renderFeedbackListItem(feedback))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        No feedback found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination 
              count={Math.ceil(total / rowsPerPage)} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        </Paper>
        
        {/* Snackbar for copy success */}
        <Snackbar
          open={!!copySuccessMessage}
          autoHideDuration={3000}
          onClose={() => setCopySuccessMessage(null)}
          message={copySuccessMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
        
        {/* GCC Details Modal */}
        <Modal
          open={gccModalOpen}
          onClose={handleCloseGccModal}
          aria-labelledby="gcc-modal-title"
          aria-describedby="gcc-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1000,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: 1, 
              borderColor: 'divider',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText'
            }}>
              <Typography id="gcc-modal-title" variant="h6" component="h2">
                Guest Comment Card Details
              </Typography>
              <Typography variant="subtitle2" color="primary.contrastText">
                Reference Number: {selectedGccId}
              </Typography>
            </Box>
            
            {loadingGccDetail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : !gccDetail ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="error">No data found for this reference number.</Alert>
              </Box>
            ) : (
              <>
                {/* Tabs for different sections */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={activeGccTab} 
                    onChange={(_, newValue) => setActiveGccTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Customer Information" />
                    <Tab label="Feedback Responses" />
                    <Tab label="Outlet Details" />
                  </Tabs>
                </Box>
                
                {/* Tab Content: Customer Information */}
                {activeGccTab === 0 && (
                  <Box sx={{ p: 3, overflow: 'auto' }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                                {gccDetail.customer_name?.charAt(0) || 'G'}
                              </Avatar>
                              <Box>
                                <Typography variant="h6">{gccDetail.customer_name || 'Anonymous'}</Typography>
                                {gccDetail.customer_phone && (
                                  <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                                    <PersonIcon fontSize="small" />
                                    {gccDetail.customer_phone}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Sales Number</Typography>
                                <Typography variant="body1">{gccDetail.sales_number || '-'}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Submit Date</Typography>
                                <Typography variant="body1">{formatDate(gccDetail.submit_date)}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                <Box mt={0.5}>{getStatusChip(gccDetail.status)}</Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Brand & Outlet</Typography>
                            
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <StoreIcon color="primary" />
                              <Typography variant="body1">{gccDetail.brand_name || '-'}</Typography>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary">Outlet</Typography>
                            <Typography variant="body1" gutterBottom>{gccDetail.outlet_name || '-'}</Typography>
                            
                            {gccDetail.outlet_address && (
                              <>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Address</Typography>
                                <Typography variant="body1">{gccDetail.outlet_address}</Typography>
                              </>
                            )}
                            
                            {gccDetail.outlet_phone && (
                              <>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Phone</Typography>
                                <Typography variant="body1">{gccDetail.outlet_phone}</Typography>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                
                {/* Tab Content: Feedback Responses */}
                {activeGccTab === 1 && (
                  <Box sx={{ p: 3, overflow: 'auto' }}>
                    {gccDetail.questionnaire.length === 0 ? (
                      <Alert severity="info">No feedback responses found.</Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {gccDetail.questionnaire.map((item, index) => (
                          <Grid item xs={12} key={index}>
                            <Card variant="outlined" sx={{ 
                              mb: 2,
                              borderColor: item.pooranswer ? 'error.light' : 'success.light',
                              borderWidth: item.pooranswer ? 2 : 1
                            }}>
                              <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                  Question {index + 1}: {item.question}
                                </Typography>
                                
                                <Box sx={{ 
                                  p: 2, 
                                  bgcolor: 'action.hover', 
                                  borderRadius: 1,
                                  mt: 1
                                }}>
                                  <Typography variant="body2" color="text.secondary">Answer:</Typography>
                                  <Typography variant="body1">{item.answer || '-'}</Typography>
                                </Box>
                                
                                {item.pooranswer && (
                                  <Box sx={{ 
                                    p: 2, 
                                    bgcolor: 'error.lighter', 
                                    borderRadius: 1,
                                    mt: 2,
                                    border: 1,
                                    borderColor: 'error.light'
                                  }}>
                                    <Typography variant="body2" color="error">Poor Feedback:</Typography>
                                    <Typography variant="body1">{item.pooranswer}</Typography>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                )}
                
                {/* Tab Content: Outlet Details */}
                {activeGccTab === 2 && (
                  <Box sx={{ p: 3, overflow: 'auto' }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Outlet Information</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">Name</Typography>
                            <Typography variant="body1" gutterBottom>{gccDetail.outlet_name || '-'}</Typography>
                            
                            {gccDetail.outlet_address && (
                              <>
                                <Typography variant="body2" color="text.secondary">Address</Typography>
                                <Typography variant="body1" gutterBottom>{gccDetail.outlet_address}</Typography>
                              </>
                            )}
                            
                            {gccDetail.outlet_phone && (
                              <>
                                <Typography variant="body2" color="text.secondary">Phone</Typography>
                                <Typography variant="body1">{gccDetail.outlet_phone}</Typography>
                              </>
                            )}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">Brand</Typography>
                            <Typography variant="body1">{gccDetail.brand_name || '-'}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </>
            )}
            
            {/* Modal Footer */}
            <Box sx={{ 
              p: 2, 
              borderTop: 1, 
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button variant="contained" onClick={handleCloseGccModal}>
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
        
        {/* Dialogs */}
        <EditDialog
          open={editDialogOpen}
          comment={selectedComment}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleEditSave}
        />
        
        <ComplaintStatusDialog
          open={statusDialogOpen}
          feedback={selectedComment}
          onClose={() => setStatusDialogOpen(false)}
          onSave={handleStatusSave}
        />
        
        <ComplaintHistoryDialog
          open={historyDialogOpen}
          feedback={selectedComment}
          onClose={() => setHistoryDialogOpen(false)}
        />

        <FeedbackDetailDialog
          open={detailDialogOpen}
          feedback={selectedComment}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedComment(null);
          }}
          onViewGcc={(feedback) => {
            setDetailDialogOpen(false);
            void handleGccClick(feedback);
          }}
        />
      </Box>
    </Container>
  );
};

export default ComplaintManagementPage; 